import {
    PropertiesPanelContent,
    PropertiesPanelPropertyConfig
} from 'src/components/pages/productConfiguration/steps/configurator-components/scene-properties-panel';
import { T } from 'src/shared/helpers';
import { createTripleNumberConfig } from './propertyConfigs';

export enum CameraProperty {
    CAMERA_POSITION_FOV,
    CAMERA_HEIGHT,
    CAMERA_LIMIT_ZOOM,
    CAMERA_ENABLE_LIMIT_MIN_ZOOM,
    CAMERA_ENABLE_LIMIT_MAX_ZOOM,
    CAMERA_LIMIT_MIN_ZOOM,
    CAMERA_LIMIT_MAX_ZOOM,
    CAMERA_LOCK_HORIZONTAL,
    CAMERA_LOCK_VERTICAL,
    CAMERA_MIN_ALPHA,
    CAMERA_MAX_ALPHA,
    CAMERA_MIN_BETA,
    CAMERA_MAX_BETA,
    CAMERA_AUTO_ROTATE,

    /** Non è un valore che cambia: è la richiesta di rimetterli tutti come erano all'inizio. */
    CAMERA_RESET
}

/**
 * COM'ERANO ALL'INIZIO, dichiarato una volta sola. Non sono numeri scelti adesso: sono gli stessi
 * su cui il pannello ricade quando la scena non ha mai avuto un valore suo, quindi «Reset» rimette
 * davvero le cose come se nessuno le avesse toccate — invece di inventare un secondo inizio.
 *
 * `fov` in radianti e gli zoom in frazioni, come li vuole il motore: la conversione in gradi e in
 * centesimi la fa chi li mostra.
 */
export const CAMERA_DEFAULTS = {
    fov: 1,
    height: 0,
    minZoom: 0,
    maxZoom: 1
};

/**
 * LA SCHEDA DI UNA CAMERA SALVATA mostra ciò che una camera salvata HA: dov'è e cosa guarda. Il
 * motore ne conserva sette campi — identificativo, nome, `px py pz`, `tx ty tz` — e non uno di più.
 *
 * QUELLO CHE MANCA NON È UNA DIMENTICANZA. Campo visivo, zoom, limiti e rotazione automatica il
 * mockup li disegna qui, dentro ogni camera; il motore li tiene sulla SCENA. Metterli qui
 * prometterebbe che sono di questa camera: otto camere mostrerebbero otto copie dello stesso numero,
 * e toccarne una le cambierebbe tutte. Stanno dove sono davvero, in «Free camera».
 *
 * E NON C'È LA ROTAZIONE. Una camera salvata è definita da dov'è e da cosa guarda: come è girata è la
 * conseguenza, non un dato. I primi due assi si potrebbero calcolare muovendo il bersaglio, il terzo —
 * il rollio — non ha dove esistere.
 */
export type CameraLocationNode = {
    position: [number, number, number];
    target: [number, number, number];
};

export type CameraLocationHelpers = {
    onChange: (what: 'position' | 'target', axis: 'x' | 'y' | 'z', value: number) => void;
};

export const useCameraLocationPropertiesCreator =
    (): ((camera: CameraLocationNode, helpers: CameraLocationHelpers) => PropertiesPanelContent[]) =>
    (camera: CameraLocationNode, helpers: CameraLocationHelpers) => [
        createPropertyConfig({
            type: 'section',
            id: 'camera-location-scene',
            title: T._('Scene', 'Admin'),
            properties: [
                createPropertyConfig({
                    type: 'group',
                    id: 'transform',
                    header: T._('Transform', 'Admin'),
                    defaultIsOpen: true,
                    properties: [
                        createTripleNumberConfig('position', T._('Position', 'Admin'), camera.position, (axis, value) =>
                            helpers.onChange('position', axis, value)
                        ),

                        // IL BERSAGLIO È IL PUNTO CHE LA CAMERA GUARDA, e sta sotto la posizione perché si
                        // legge dopo: prima dov'è, poi verso dove. Il mockup lo chiama «Target» e gli dà tre
                        // modi — spento, legato a una mesh, a mano. Qui può essere solo a mano: gli altri due
                        // vorrebbero un campo che la camera salvata non ha.
                        createTripleNumberConfig('target', T._('Target', 'Admin'), camera.target, (axis, value) => helpers.onChange('target', axis, value))
                    ]
                })
            ]
        })
    ];

export type CameraConfigHelpers = {
    onPropertyChange: (property: CameraProperty, value?: any) => void;
};

export type CameraNode = {
    id: string;
    name: string;
    position: [number, number, number];
    target: [number, number, number];
    fov: number;
    isActive: boolean;
    enableZoomLimit: boolean;
    // Proprietà zoom
    useMinZoomPerc: boolean;
    useMaxZoomPerc: boolean;
    minZoomPerc: number;
    maxZoomPerc: number;
    // Proprietà rotazione
    lowerBetaLimits: number | undefined;
    upperBetaLimits: number | undefined;
    lowerAlphaLimit: number | undefined;
    upperAlphaLimit: number | undefined;
    height: number;
    autoRotate: boolean;
};

const createPropertyConfig = <T extends PropertiesPanelContent>(config: T) => config;

// Configurazione per il gruppo delle proprietà di base della camera
const createBasicCameraPropertiesGroup = (camera: CameraNode, helpers: CameraConfigHelpers): PropertiesPanelPropertyConfig => {
    return createPropertyConfig({
        type: 'group',
        id: 'camera-properties',
        header: T._('Camera', 'Admin'),
        defaultIsOpen: true,
        properties: [
            // IL CAMPO VISIVO SI LEGGE IN GRADI, e si scrive in radianti. Il motore tiene il valore di
            // Babylon, che è in radianti: mostrarlo così com'è dava una scala da 0 a 2 che non vuol dire
            // niente per chi la guarda — un obiettivo si sceglie in gradi. La conversione sta QUI, al
            // confine fra ciò che si legge e ciò che si salva, e non nel motore né nello stato.
            //
            // Da 10 a 120 gradi: è la scala del mockup, ed è quella entro cui una inquadratura ha senso.
            createPropertyConfig({
                type: 'range',
                id: 'fov',
                label: T._('FOV', 'Admin'),
                value: Math.round((camera.fov * 180) / Math.PI),
                min: 10,
                max: 120,
                step: 1,
                onChange: (value) => helpers.onPropertyChange(CameraProperty.CAMERA_POSITION_FOV, (value * Math.PI) / 180)
            }),

            // Height
            createPropertyConfig({
                type: 'range',
                id: 'height',
                label: T._('Height', 'Admin'),
                value: camera.height,
                min: 0,
                max: 10,
                step: 0.1,
                onChange: (value) => helpers.onPropertyChange(CameraProperty.CAMERA_HEIGHT, value)
            }),

            // LA ROTAZIONE AUTOMATICA È UN FATTO DELLA CAMERA, non del palco: è il punto di vista che
            // gira intorno al modello, e il modello resta fermo dov'è. Stava fra i tastini sopra la
            // scena, dove vivono i modi di GUARDARE — griglia, esplosione, unità — e da lì prometteva
            // di essere una cosa del momento, mentre si salva nella scena come tutte le altre
            // impostazioni delle camere. Il valore è sempre stato qui: era il comando a stare altrove.
            createPropertyConfig({
                type: 'switch',
                id: 'auto-rotate',
                label: T._('Auto rotate', 'Admin'),
                checked: camera.autoRotate,
                onChange: (checked) => helpers.onPropertyChange(CameraProperty.CAMERA_AUTO_ROTATE, checked)
            })
        ]
    });
};

// Configurazione per il gruppo dei limiti di zoom
const createZoomLimitsGroup = (camera: CameraNode, helpers: CameraConfigHelpers): PropertiesPanelPropertyConfig => {
    const properties: PropertiesPanelPropertyConfig[] = [
        // Abilita/disabilita zoom limit
        createPropertyConfig({
            type: 'switch',
            id: 'enable-zoom-limit',
            label: T._('Zoom limit', 'Admin'),
            checked: camera.enableZoomLimit,
            onChange: (checked) => helpers.onPropertyChange(CameraProperty.CAMERA_LIMIT_ZOOM, checked)
        })
    ];

    // Se zoom limit è abilitato, aggiungi i controlli
    if (camera.enableZoomLimit) {
        // Min zoom
        properties.push(
            createPropertyConfig({
                type: 'switch',
                id: 'enable-min-zoom',
                label: T._('Enable the minimum zoom limit', 'Admin'),
                checked: camera.useMinZoomPerc,
                onChange: (checked) => helpers.onPropertyChange(CameraProperty.CAMERA_ENABLE_LIMIT_MIN_ZOOM, checked)
            })
        );

        if (camera.useMinZoomPerc) {
            properties.push(
                createPropertyConfig({
                    type: 'range',
                    id: 'min-zoom-perc',
                    label: T._('Min', 'Admin'),
                    value: camera.minZoomPerc,
                    min: 0,
                    max: 100,
                    step: 1,
                    onChange: (value) => helpers.onPropertyChange(CameraProperty.CAMERA_LIMIT_MIN_ZOOM, value / 100)
                })
            );
        }

        // Max zoom
        properties.push(
            createPropertyConfig({
                type: 'switch',
                id: 'enable-max-zoom',
                label: T._('Enable the maximum zoom limit', 'Admin'),
                checked: camera.useMaxZoomPerc,
                onChange: (checked) => helpers.onPropertyChange(CameraProperty.CAMERA_ENABLE_LIMIT_MAX_ZOOM, checked)
            })
        );

        if (camera.useMaxZoomPerc) {
            properties.push(
                createPropertyConfig({
                    type: 'range',
                    id: 'max-zoom-perc',
                    label: T._('Max', 'Admin'),
                    value: camera.maxZoomPerc,
                    min: 0,
                    max: 100,
                    step: 1,
                    onChange: (value) => helpers.onPropertyChange(CameraProperty.CAMERA_LIMIT_MAX_ZOOM, value / 100)
                })
            );
        }
    }

    return createPropertyConfig({
        type: 'group',
        id: 'zoom-limits',
        header: T._('Zoom limits', 'Admin'),
        defaultIsOpen: false,
        properties
    });
};

// Configurazione per il gruppo dei limiti di rotazione
const createRotationLimitsGroup = (camera: CameraNode, helpers: CameraConfigHelpers): PropertiesPanelPropertyConfig => {
    const isAlphaLimited = camera.upperAlphaLimit != null && camera.lowerAlphaLimit != null;
    const isBetaLimited = camera.upperBetaLimits != null && camera.lowerBetaLimits != null;

    const properties: PropertiesPanelPropertyConfig[] = [
        // Horizontal rotation limit
        createPropertyConfig({
            type: 'switch',
            id: 'horizontal-rotation-limit',
            label: T._('Horizontal rotation limit', 'Admin'),
            checked: isAlphaLimited,
            onChange: (checked) => helpers.onPropertyChange(CameraProperty.CAMERA_LOCK_HORIZONTAL, checked)
        })
    ];

    if (isAlphaLimited) {
        properties.push(
            createPropertyConfig({
                type: 'range',
                id: 'min-alpha',
                label: T._('Min', 'Admin'),
                value: Math.round((camera.lowerAlphaLimit || 0) * (180 / Math.PI)),
                min: -180,
                max: 180,
                step: 1,
                onChange: (value) => helpers.onPropertyChange(CameraProperty.CAMERA_MIN_ALPHA, value * (Math.PI / 180))
            }),
            createPropertyConfig({
                type: 'range',
                id: 'max-alpha',
                label: T._('Max', 'Admin'),
                value: Math.round((camera.upperAlphaLimit || 0) * (180 / Math.PI)),
                min: -180,
                max: 180,
                step: 1,
                onChange: (value) => helpers.onPropertyChange(CameraProperty.CAMERA_MAX_ALPHA, value * (Math.PI / 180))
            })
        );
    }

    // Vertical rotation limit
    properties.push(
        createPropertyConfig({
            type: 'switch',
            id: 'vertical-rotation-limit',
            label: T._('Vertical rotation limit', 'Admin'),
            checked: isBetaLimited,
            onChange: (checked) => helpers.onPropertyChange(CameraProperty.CAMERA_LOCK_VERTICAL, checked)
        })
    );

    if (isBetaLimited) {
        properties.push(
            createPropertyConfig({
                type: 'range',
                id: 'min-beta',
                label: T._('Min', 'Admin'),
                value: Math.round((camera.lowerBetaLimits || 0) * (180 / Math.PI) - 90),
                min: -90,
                max: 90,
                step: 1,
                onChange: (value) => helpers.onPropertyChange(CameraProperty.CAMERA_MIN_BETA, (value + 90) * (Math.PI / 180))
            }),
            createPropertyConfig({
                type: 'range',
                id: 'max-beta',
                label: T._('Max', 'Admin'),
                value: Math.round((camera.upperBetaLimits || 0) * (180 / Math.PI) - 90),
                min: -90,
                max: 90,
                step: 1,
                onChange: (value) => helpers.onPropertyChange(CameraProperty.CAMERA_MAX_BETA, (value + 90) * (Math.PI / 180))
            })
        );
    }

    return createPropertyConfig({
        type: 'group',
        id: 'rotation-limits',
        header: T._('Rotation limits', 'Admin'),
        defaultIsOpen: false,
        properties
    });
};

// LA COLONNA HA DUE CAPITOLI. «Basic Properties» non diceva niente — sono le proprietà di base
// di che cosa? — e i due gruppi dei limiti stavano allo stesso livello di quello che descrive la
// camera, come se limitare uno zoom fosse una caratteristica dell'obiettivo.
//
// Il criterio è quello del mockup: sopra la camera come è fatta, sotto le regole che le si danno
// — i limiti sono impostazioni, non ottica.
const cameraPropertiesByType = (camera: CameraNode, helpers: CameraConfigHelpers): PropertiesPanelContent[] => {
    return [
        createPropertyConfig({
            type: 'section',
            id: 'camera-scene',
            title: T._('Scene', 'Admin'),
            properties: [createBasicCameraPropertiesGroup(camera, helpers)]
        }),
        createPropertyConfig({
            type: 'section',
            id: 'camera-settings',
            title: T._('Settings', 'Admin'),
            properties: [
                createZoomLimitsGroup(camera, helpers),
                createRotationLimitsGroup(camera, helpers),

                // FUORI DAI DUE GRUPPI, e in fondo. Dentro uno dei due sembrerebbe azzerare quello e
                // basta; qui attraversa la colonna e si legge dopo aver visto tutto ciò che rimette a
                // posto. Il nome dice il perimetro, perché un «Reset» da solo non lo direbbe.
                createPropertyConfig({
                    type: 'action',
                    id: 'camera-reset',
                    label: T._('Reset all camera settings', 'Admin'),
                    onClick: () => helpers.onPropertyChange(CameraProperty.CAMERA_RESET)
                })
            ]
        })
    ];
};

// Hook principale da esportare (simile a useObjectPropertiesCreator)
export const useCameraPropertiesCreator = (): ((camera: CameraNode, helpers: CameraConfigHelpers) => PropertiesPanelContent[]) => {
    return (camera: CameraNode, helpers: CameraConfigHelpers) => {
        return cameraPropertiesByType(camera, helpers);
    };
};
